import type { YearValue } from "@/lib/data/types";
import { formatCompactCurrency, formatCompactNumber, formatPercent, formatRatio } from "@/lib/format";

export type TrendValueFormat = "ratio" | "percent" | "currency" | "number";

function formatValue(format: TrendValueFormat, v: number, currency: string): string {
  if (format === "percent") return formatPercent(v);
  if (format === "currency") return formatCompactCurrency(v, currency);
  if (format === "number") return formatCompactNumber(v);
  return formatRatio(v);
}

/** Unterhalb dieser Schwelle gilt eine Kennzahl als "weitgehend stabil" statt
 * gestiegen/gesunken - vermeidet eine irrefuehrende Trendaussage bei Rauschen. */
const STABLE_THRESHOLD = 0.05;

/**
 * Kurzer, rein deskriptiver Ein-Satz-Trendhinweis fuer eine Jahres-Kennzahlenreihe -
 * reine Funktion, kein I/O, kein KI-Aufruf (bewusste Design-Entscheidung: ein
 * deterministischer Satz ist bei einer simplen Start-zu-Ende-Trendaussage genauso
 * aussagekraeftig wie ein KI-Aufruf, aber sofort verfuegbar, kostenlos und ohne
 * Halluzinationsrisiko - analog zur Entscheidung beim Score-Kurztext, siehe
 * lib/scoring/scoring.ts -> buildSummary()). Nennt IMMER die Einheit (ueber
 * formatValue), NIE Handlungssprache. Nur ab `minYearsSpan` Jahren Historie sinnvoll
 * (Erik 2026-07-11: "ab 5 oder 10 Jahren") - sonst `null` (Chart zeigt dann keinen
 * Trendsatz, statt eine kurze Zeitspanne ueberzuinterpretieren).
 */
export function describeTrend(
  label: string,
  series: YearValue[],
  format: TrendValueFormat,
  currency = "USD",
  minYearsSpan = 5,
): string | null {
  const sorted = [...series].sort((a, b) => a.year - b.year);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (!first || !last || last.year - first.year < minYearsSpan) return null;
  if (first.value === 0) return null;

  const change = (last.value - first.value) / Math.abs(first.value);
  const yearsSpan = last.year - first.year;
  const firstLabel = formatValue(format, first.value, currency);
  const lastLabel = formatValue(format, last.value, currency);

  if (Math.abs(change) < STABLE_THRESHOLD) {
    return `${label} blieb von ${first.year} bis ${last.year} weitgehend stabil bei rund ${lastLabel}.`;
  }

  const verb = change > 0 ? "stieg" : "sank";
  const changeLabel = formatPercent(Math.abs(change));
  return `${label} ${verb} von ${firstLabel} (${first.year}) auf ${lastLabel} (${last.year}) - ${change > 0 ? "ein Anstieg" : "ein Rückgang"} um ${changeLabel} über ${yearsSpan} Jahre.`;
}
